<template>
  <div class="flex flex-col gap-4" :data-testid="testId">
    <div v-for="entry in entries" :key="entry.field.id" class="flex flex-col gap-1.5">
      <label :for="inputId(entry.field.key)" class="text-sm font-semibold text-surface-700">
        {{ entry.field.label }}<span v-if="entry.field.is_required" class="text-red-500" aria-hidden="true"> *</span>
        <Tag v-if="labelTags[entry.field.key]" :value="labelTags[entry.field.key]" severity="warn" class="ml-2 align-middle !text-xs"
          :data-testid="`custom-field-tag-${entry.field.key}`" />
      </label>
      <CustomFieldInput v-model="model[entry.field.key]" :field="entry.field" :inputId="inputId(entry.field.key)"
        :label="entry.field.label" :invalid="!!errors[entry.field.key]"
        :dependsOnValue="entry.field.depends_on ? model[entry.field.depends_on] : undefined"
        :dependsOnLabel="parentLabel(entry.field.depends_on)" :maxLength="maxLengths[entry.field.key]" />
      <small v-if="errors[entry.field.key]" class="text-red-500" :data-testid="`custom-field-error-${entry.field.key}`">
        {{ errors[entry.field.key] }}
      </small>
      <small v-else-if="entry.field.description" class="text-surface-500">{{ entry.field.description }}</small>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { CustomFieldOptionsSource } from '@/types/composition-functions';
import type { CustomFieldValueFieldsFragment } from '@/types/operations';
import CustomFieldInput from '@/components/common/CustomFieldInput.vue';
import { useNigerianStates } from '@/composables/queries/useNigerianStates';
import { dependentsToClear, parentFieldKeys, type CustomFieldInputValue } from '@/utils/customFields';

/**
 * The inputs for a set of custom fields (#574), one per field with its
 * required marker, help text and inline error. Shared by the profile card's
 * edit dialog and the create forms (admit student, new guardian), so a field
 * looks and validates the same everywhere.
 */
const props = withDefaults(defineProps<{
  entries: CustomFieldValueFieldsFragment[];
  /** Messages keyed by field key. */
  errors?: Record<string, string>;
  idPrefix?: string;
  testId?: string;
  /** Longest text accepted, keyed by field key, where it differs from the default. */
  maxLengths?: Record<string, number>;
  /** A short tag shown beside a field's label, keyed by field key. */
  labelTags?: Record<string, string>;
}>(), { errors: () => ({}), idPrefix: 'cf', testId: 'custom-fields-form', maxLengths: () => ({}), labelTags: () => ({}) });

const model = defineModel<Record<string, CustomFieldInputValue>>({ required: true });

const inputId = (key: string) => `${props.idPrefix}-${key}`;

const parentLabel = (key: string | null | undefined) =>
  (key ? props.entries.find(e => e.field.key === key)?.field.label : undefined);

// An LGA that follows a state field is cleared when the user picks a state it
// is not in. A refill (the form loading a record's stored values) replaces the
// whole model instead: what was loaded stays, even an LGA outside the list.
const parents = computed(() => parentFieldKeys(props.entries));
const followsList = computed(() => props.entries.some(e =>
  e.field.options_source === CustomFieldOptionsSource.NigerianLgas && !!e.field.depends_on));
const { states } = useNigerianStates({ enabled: followsList });

let watchedModel = model.value;
watch(() => parents.value.map(key => model.value[key]), (next, previous) => {
  if (model.value !== watchedModel) {
    watchedModel = model.value;
    return;
  }
  const changed = parents.value.filter((_, i) => next[i] !== previous[i]);
  for (const key of dependentsToClear(props.entries, model.value, changed, states.value)) {
    model.value[key] = null;
  }
});
</script>
